import { BackrsFormField } from '@backrs_ui/common-ui/src/molecules/BackrsForm/types';

import { FilterOptionsState } from '@mui/material';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';

import { OptionType } from '../types';

type AutocompleteValue = string | OptionType | (string | OptionType)[] | null;

/**
 * Determines if an option is equal to a value for autocomplete comparison
 */
export const determineIsOptionEqualToValue = (
  option: OptionType,
  value: OptionType | OptionType[],
  field: BackrsFormField,
): boolean => {
  if (Array.isArray(value) && field.autoCompleteEndpoint) {
    return value.some((v) => v.value === option.value);
  }

  if (Array.isArray(value)) {
    return value.some((v) => v.value === option.value);
  }

  // value is selection, option is list of possibilities
  if (option.value === value.value) {
    return true;
  }

  return false;
};

/**
 * Filters autocomplete options based on input and field configuration
 */
export const handleFilterOptions = (
  options: OptionType[],
  state: FilterOptionsState<OptionType>,
  field: BackrsFormField,
): OptionType[] => {
  if (field.manualFilterOptions) {
    return options.filter((option) =>
      option.label.toLowerCase().includes(state.inputValue.toLowerCase()),
    );
  } else if (state.inputValue.length < 4 && field.autoCompleteEndpoint) {
    return [];
  }

  return options;
};

/**
 * Transforms autocomplete options to string values for form submission
 */
export const transformOptionsToValues = (options: AutocompleteValue): string | string[] => {
  if (!options) return '';
  if (Array.isArray(options)) {
    return options.map((option) => (typeof option === 'string' ? option : option.value));
  }

  return typeof options === 'string' ? options : options.value;
};

/**
 * Gets selected options in a consistent array format
 */
export const getSelectedOptions = (
  selectedOptions: OptionType | OptionType[] | undefined,
  isMultiple: boolean,
): OptionType[] => {
  if (isMultiple) {
    return Array.isArray(selectedOptions) ? selectedOptions : [];
  }

  return selectedOptions && !Array.isArray(selectedOptions) ? [selectedOptions] : [];
};

/**
 * Creates option objects from form values that may not exist in the options list
 */
export const createValueOptionsFromForm = (
  controllerField: ControllerRenderProps<FieldValues, string>,
  sourceOptions: OptionType[],
  isMultiple: boolean,
): OptionType[] => {
  if (!isMultiple || !Array.isArray(controllerField.value)) {
    return [];
  }

  return controllerField.value.map((value) => {
    // First try to find in source options
    const existingOption = sourceOptions.find((opt) => opt.value === value);

    if (existingOption) return existingOption;

    // If not found, find by label instead
    const optionByLabel = sourceOptions.find((opt) => opt.label === value);

    if (optionByLabel) return optionByLabel;

    // If still not found, create a new option
    return { label: value, value };
  });
};

/**
 * Finds the selected option for single select autocomplete
 */
export const findSelectedOption = (
  controllerField: ControllerRenderProps<FieldValues, string>,
  sourceOptions: OptionType[],
): OptionType | null => {
  const val = Array.isArray(controllerField.value)
    ? controllerField.value[0]
    : controllerField.value;

  return (
    sourceOptions.find((option) => option.value === (typeof val === 'object' ? val?.value : val)) ||
    null
  );
};

/**
 * Merges new options with existing options, avoiding duplicates
 */
export const mergeOptionsWithoutDuplicates = (
  existingOptions: OptionType[],
  newOptions: OptionType[],
): OptionType[] => {
  const updatedOptions = [...existingOptions];

  newOptions.forEach((option) => {
    if (!updatedOptions.some((o) => o.value === option.value)) {
      updatedOptions.push(option);
    }
  });

  return updatedOptions;
};

/**
 * Creates new options from autocomplete values
 */
export const createOptionsFromAutocompleteValues = (newValue: AutocompleteValue): OptionType[] => {
  if (!Array.isArray(newValue)) {
    return [];
  }

  return newValue.map((option) =>
    typeof option === 'string' ? { label: option, value: option } : option,
  );
};
